import {
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Table,
  TablePaginationConfig,
  Tag,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { notifyError, notifySuccess, showConfirm } from '../../../store/utils';
import { API } from '../../../api';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import ProvinceSelect from '../../../lib/ProvinceSelect';
import DistrictSelect from '../../../lib/DistrictSelect';
import StreetSelect from '../../../lib/StreetSelect';
import queryString from 'query-string';

interface State {
  loading: boolean;
  branch: any;
}

interface AddressType {
  list: any[];
  total: number;
  page: number;
  size: number;
}

const BranchAddress = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { branchId } = useParams();
  const [state, updateState] = useState<State>({
    loading: true,
    branch: {},
  });
  const [address, setAddress] = useState<AddressType>({
    list: [],
    total: 0,
    page: 1,
    size: 10,
  });
  const [visible, updateVisible] = useState<boolean>(false);

  const fetchBranch = useCallback(
    async (branchId: string) => {
      try {
        updateState({ ...state, loading: true });
        const {
          result: { data },
        } = await API.get({ apiVersion: 'api' })(`/branch/${branchId}`);
        updateState({
          loading: false,
          branch: data,
        });
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [branchId]
  );

  const fetchBranchAddress = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/branch/address/${branchId}${location.search}`
      );
      console.log('fetchBranchAddress', result);
      setAddress({
        list: result?.data || [],
        total: result.total || 0,
        page: result.page || 1,
        size: result?.size || 10,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [branchId, location]);

  useEffect(() => {
    if (branchId) {
      fetchBranch(branchId);
    }
  }, [branchId]);

  useEffect(() => {
    console.log('location.search', location.search);
    if (location.search) {
      fetchBranchAddress();
    }
  }, [location.search, branchId]);

  useEffect(() => {
    if (!location.search) {
      navigate(`/settings/branch/address/${branchId}?page=1&size=10`);
    }
  }, [location, branchId]);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      const params = queryString.parse(location.search);
      const search = {
        ...params,
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      };
      navigate(`/settings/branch/address/${branchId}?${queryString.stringify(search)}`);
    },
    [location, branchId]
  );

  const showDeleteConfirm = useCallback(
    (record: any) => {
      showConfirm({
        title: 'Устгахдаа итгэлтэй байна уу?',
        content: 'Уг үйлдэл нь буцаагдахгүй!. Бүсийн тохиргоо бүгд устахыг анхаарна уу',
        onOk() {
          addressItemDelete(record.no);
        },
        okText: 'Тийм',
        cancelText: 'Үгүй',
      });
    },
    [branchId, location]
  );

  const addressItemDelete = useCallback(
    async (branchAddressNo: number) => {
      try {
        await API.delete({ apiVersion: 'api' })(`/branch/address/${branchId}/${branchAddressNo}`);
        notifySuccess('Амжилттай устлаа');
        fetchBranchAddress();
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [branchId, location]
  );

  const columns = [
    {
      title: 'Зип код',
      key: 'zipcode',
      render: (record: any) => record?.street?.zipcode,
    },
    {
      title: 'Аймаг хот',
      key: 'district',
      render: (record: any) => record?.street?.province?.name,
    },
    {
      title: 'Сум дүүрэг',
      key: 'sumDuureg',
      render: (record: any) => record?.street?.district?.name,
    },
    {
      title: 'Баг хороо',
      key: 'name',
      render: (record: any) => record?.street?.name,
    },
    {
      title: 'Хүргэлт',
      key: 'delivery',
      render: (record: any) => record?.deliveryType?.text || '',
    },
    {
      title: 'Үйлдэл',
      width: 120,
      render: (val: any, row: any) => (
        <span>
          <Button
            type="primary"
            danger
            size="small"
            onClick={() => showDeleteConfirm(row)}
            icon={<DeleteOutlined />}
          >
            Устгах
          </Button>
        </span>
      ),
    },
  ];

  return (
    <Card title={`Салбарын хаяг`} loading={state.loading}>
      <Alert
        message="Салбарын мэдээлэл"
        description={`Нэр: ${state?.branch?.name} (${state?.branch?.cd})`}
        type="info"
      />
      <Divider />
      <Flex gap={24} justify="end" style={{ marginBottom: 24 }}>
        <Button icon={<PlusOutlined />} type="primary" onClick={() => updateVisible(true)}>
          Хаяг нэмэх
        </Button>
      </Flex>
      <Table
        bordered
        columns={columns}
        size="small"
        pagination={{
          current: address?.page || 1,
          size: 'small',
          pageSize: address?.size || 10,
          total: address?.total,
          position: ['bottomRight'],
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        dataSource={address.list || []}
      />
      <BranchAddressUpdate
        visible={visible}
        branchNo={branchId}
        onClose={() => updateVisible(false)}
        onReload={() => fetchBranchAddress()}
      />
    </Card>
  );
};

export default BranchAddress;

const BranchAddressUpdate = ({
  visible,
  onClose,
  branchNo,
  onReload,
}: {
  visible: boolean;
  branchNo?: string;
  onClose: () => void;
  onReload?: () => void;
}) => {
  const [form] = Form.useForm();
  const [loading, updateLoading] = useState(false);

  const onFinish = useCallback(
    async (values: any) => {
      try {
        updateLoading(true);
        await API.post({ apiVersion: 'api' })(`/branch/address/create/${branchNo}`, values);
        notifySuccess('Амжилттай хадгаллаа');
        if (onReload) onReload();
        close();
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        updateLoading(false);
      }
    },
    [branchNo]
  );

  const close = useCallback(() => {
    form.resetFields();
    onClose();
  }, []);

  const provinceHandler = useCallback(
    (value?: string) => {
      form.setFieldsValue({
        districtNo: undefined,
        streetNo: undefined,
      });
    },
    [form]
  );

  const disrictHandler = useCallback(
    (value?: string) => {
      form.setFieldValue('streetNo', undefined);
    },
    [form]
  );

  return (
    <Modal title="Салбарын хаяг бүртгэл" open={visible} onCancel={close} footer={null}>
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="provinceNo"
          label="Аймаг хот"
          rules={[{ required: true, message: 'Аймаг хот сонгоно уу!' }]}
        >
          <ProvinceSelect
            showSearch
            allowClear
            onChange={(value?: string) => provinceHandler(value)}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.provinceNo !== currentValues.provinceNo
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('provinceNo') ? (
              <Form.Item
                name="districtNo"
                label="Сум дүүрэг"
                rules={[{ required: true, message: 'Сум дүүрэг сонгоно уу!' }]}
              >
                <DistrictSelect
                  onChange={(value?: string) => disrictHandler(value)}
                  provinceNo={getFieldValue('provinceNo')}
                  allowClear
                />
              </Form.Item>
            ) : (
              <Form.Item label="Сум дүүрэг">
                <DistrictSelect allowClear />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.districtNo !== currentValues.districtNo
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('districtNo') ? (
              <Form.Item name="streetNo" label="Баг хороо">
                <StreetSelect showSearch districtNo={getFieldValue('districtNo')} allowClear />
              </Form.Item>
            ) : (
              <Form.Item label="Баг хороо">
                <StreetSelect />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item
          name="deliveryType"
          label="Хүргэлт"
          rules={[{ required: true, message: 'Хүргэлт сонгоно уу!' }]}
        >
          <Select
            options={[
              {
                label: 'Гарт олгох',
                value: 'HOMEDELIVERY',
              },
              {
                label: 'Салбараас авах',
                value: 'PICKUP',
              },
              {
                label: 'Локерт үлдээх',
                value: 'LOCKER',
              },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Flex justify="end" gap={24}>
            <Button onClick={() => close()} htmlType="button">
              Болих
            </Button>
            <Button loading={loading} type="primary" htmlType="submit">
              Хадгалах
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </Modal>
  );
};
